import { Component, OnDestroy } from '@angular/core';
import { GbgrundstueckService, GrundstueckListEntry, GrundstueckSearchResponse } from '../../core/gb-api';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapAlertService } from '../../core/extension/exceptions/cap-alert.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { grundstueckSearchModeName } from '../grundstueck.constants';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { ListLoaderPreloaded } from '../../shared/components/list-loader/list-loader-preloaded';
import { KompaktGrundstueckPipe } from '../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.pipe';
import { KompaktGrundstueckbeschreibungPipe } from '../../shared/kompaktdarstellung/grundstueck-beschreibung/kompakt-grundstueckbeschreibung.pipe';
import { AbstractComponent } from '../../shared/components/abstract-component/abstract.component';
import {
  GrundstueckListEntryVm,
  GrundstueckSelection,
  GrundstueckTableComponent
} from '../../shared/widgets/grundstueck-table/grundstueck-table.component';
import { DokulinkItemBuilderFactory } from 'app/shared/components/dokulink/dokulinkItemBuilderFactory';
import { GrundstuecknummerPipe } from '../../shared/pipes/simple/grundstuecknummer.pipe';
import { SearchStateService } from '../../shared/services/search-state.service';
import { SearchMode } from '../../shared/enum/searchMode';
import { AuszugPrintService } from '../../shared/components/auszug/auszug-print-service';
import { GrundstueckAuszugSettingsComponent } from '../../shared/components/auszug/settings/grundstueck-auszug-settings.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GbPrintService } from '../../core/services/app-print.service';
import { ContentHeaderComponent } from '../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../shared/components/title-functionbar/title-functionbar.component';
import { FunctionbarSearchresultButtonsComponent } from '../../shared/components/title-functionbar/functionbar-searchresult-buttons/functionbar-searchresult-buttons.component';
import { CapSpinnerSmallComponent } from '../../shared/components/cap-spinner-small/cap-spinner-small.component';
import GrundstueckArtEnum = GrundstueckListEntry.GrundstueckArtEnum;

@Component({
  selector: 'cap-grundstueck-search-result-table',
  templateUrl: './grundstueck-search-result.component.html',
  styleUrls: ['./grundstueck-search-result.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    FunctionbarSearchresultButtonsComponent,
    GrundstueckTableComponent,
    CapSpinnerSmallComponent,
    TranslatePipe
  ]
})
export class GrundstueckSearchResultComponent extends AbstractComponent implements OnDestroy {
  grundstueckLoader: ListLoaderPreloaded<GrundstueckListEntryVm>;
  grundstuecksResponse: GrundstueckSearchResponse;
  grundstueckSubscription: Subscription;

  folio = GrundstueckArtEnum.FOLIO;
  grundstueckIds = [];

  constructor(
    private gbGrundstueckService: GbgrundstueckService,
    private stateService: SearchStateService,
    protected router: Router,
    private route: ActivatedRoute,
    private capAlertService: CapAlertService,
    private location: Location,
    private translate: TranslateService,
    private kompaktGrundstueckPipe: KompaktGrundstueckPipe,
    private kompaktGrundstueckBeschreibung: KompaktGrundstueckbeschreibungPipe,
    private grundstuecknummerPipe: GrundstuecknummerPipe,
    protected dokuLinkBuilderFactory: DokulinkItemBuilderFactory,
    private auszugService: AuszugPrintService,
    private modalService: NgbModal,
    public gbPrintService: GbPrintService
  ) {
    super();
    keysToTranslate<string>(['KeineDatenVorhanden', 'grundstueck.ListTitel', 'PlanZuGrundstueckAnzeigen']);
    this.route.params.subscribe((params) => {
      const searchMode: SearchMode = params[grundstueckSearchModeName];
      this.route.queryParams.subscribe((param) => {
        const lastSearchRequest = param['request'];
        if (lastSearchRequest) {
          const request = JSON.parse(lastSearchRequest);
          this.stateService.lastSearchMode = searchMode;
          this.grundstueckSubscription = this.getGrundstueckList(request, searchMode).subscribe({
            next: (response) => {
              this.grundstuecksResponse = response;
              if (response.recordsFound === 0) {
                this.capAlertService.info(this.translate.instant('KeineResultateGefunden'), undefined, true);
                this.navigateBackToSearch();
              }
              const gsList = response.results.map(
                (i) =>
                  new GrundstueckListEntryVm(i, {
                    translate: this.translate,
                    grundstuecknummerPipe: this.grundstuecknummerPipe,
                    kompaktGrundstueckBeschreibung: this.kompaktGrundstueckBeschreibung,
                    kompaktGrundstueckPipe: this.kompaktGrundstueckPipe,
                    dokuLinkBuilderFactory: this.dokuLinkBuilderFactory
                  })
              );
              this.grundstueckLoader = new ListLoaderPreloaded<GrundstueckListEntryVm>(15, 1, gsList, response.recordsFound);
            },
            error: (err) => {
              this.handleError(err);
            }
          });
        }
      });
    });
  }

  private getGrundstueckList(request, searchMode) {
    switch (searchMode) {
      case SearchMode.searchStandard:
        return this.gbGrundstueckService.searchGrundstueckGsNummer(request);
      case SearchMode.searchErweitert:
        return this.gbGrundstueckService.searchGrundstueckErweitert(request);
      case SearchMode.searchFachId:
        return this.gbGrundstueckService.searchGrundstueckEgrid(request);
    }
  }

  private handleError(err) {
    throw err;
  }

  navigateBackToSearch() {
    this.location.back();
  }

  ngOnDestroy(): void {
    this.grundstueckSubscription?.unsubscribe();
  }

  getZip() {
    this.selectGrundstueckSettings(this.grundstueckIds);
  }

  selectGrundstueckSettings(list) {
    const modalRef = this.modalService.open(GrundstueckAuszugSettingsComponent);
    modalRef.componentInstance.hasToZip = true;
    modalRef.componentInstance.ids = list;
    modalRef.componentInstance.indexNumber = 0;
  }

  updateCheckedGrundstueckIdList($event: GrundstueckSelection) {
    this.grundstueckIds = $event.grundstueckIds;
  }
}
